import React, { FC } from 'react';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
// @ts-ignore
import arrowOne from './assets/arrow-1-2.png';
// @ts-ignore
import arrowTwo from './assets/arrow-2-2.png';
import BadgeElement from '../badgeElement/BadgeElement';
import { DownloadFileTypes, JapaneseA4DividerPositions, PAPER_SIZE, PDFEntitlementIcon } from '../../../types/enums';
import PDFUtils from '../../utils/pdfUtils';
import { BadgeElementViewOptions, FontOption, PaperSize, PDFBadgeInfo } from '../../../types/badge';
import pdfStyles, { registerFonts } from './pdfStyles';
import EventEditionHelper from '../../../common/helpers/eventEdition/EventEditionHelper';
import { JapaneseA4Divider } from '../japaneseBadgeElement/JapaneseA4Divider';
import BadgePageDivider from '../dividing/BadgePageDivider';
import { Nullable } from "../../../types/common";

type Props = PaperSize & {
  badgeInfo: PDFBadgeInfo;
  badgeSuffix?: Nullable<string>;
  qrCodeUrl?: string;
  doubleSidedBadge?: boolean;
  base64Images: { logo: string; image1?: string; image2?: string; image3?: string };
  footerInfo: BadgeElementViewOptions;
  badgeIcons?: Array<PDFEntitlementIcon>;
  type: DownloadFileTypes;
  fonts?: FontOption[];
};

const shouldDisplayDividerFor = [PAPER_SIZE.A4, PAPER_SIZE.LETTER, PAPER_SIZE.W298_X_100, PAPER_SIZE.FOUR_X_TEN_INCH];
const PDFDocument: FC<Props> = ({
  badgeIcons = [],
  badgeSuffix,
  footerInfo,
  base64Images,
  badgeInfo,
  paperSize,
  qrCodeUrl,
  doubleSidedBadge,
  type,
  fonts
}) => {
  badgeInfo.jobTitle = badgeInfo.jobTitle || '';
  badgeInfo.city = badgeInfo.city || '';
  badgeInfo.countryCode = badgeInfo.countryCode || '';
  badgeInfo.companyDisplayName = badgeInfo.companyDisplayName || '';
  badgeInfo.stateCode = badgeInfo.stateCode || '';

  const is4x10inch = paperSize === PAPER_SIZE.FOUR_X_TEN_INCH;
  const is4x5inch = paperSize === PAPER_SIZE.FOUR_X_FIVE_INCH;
  const is105x140mm = paperSize === PAPER_SIZE.W105xH140mm;
  const is298x100mm = paperSize === PAPER_SIZE.W298_X_100;
  const isDoubleSided = is4x10inch || is298x100mm;

  registerFonts(fonts);

  const isCustomBadge = is4x10inch || is4x5inch || is105x140mm || is298x100mm;
  const isDoubleBadgeHidden = !is4x5inch && !is105x140mm;
  const isJapanese = EventEditionHelper.getIsJapaneseBusinessUnitCode();

  const getPaperSize = (paperSize: PAPER_SIZE) => {
    if (paperSize === PAPER_SIZE.A4) {
      return PAPER_SIZE.A4;
    }
    if (paperSize === PAPER_SIZE.LETTER) {
      return PAPER_SIZE.LETTER;
    }
    if (is4x5inch) {
      return { width: '4in', height: '5in' };
    }
    if (is105x140mm) {
      return { width: '105mm', height: '140mm' };
    }
    if (is298x100mm) {
      return { width: '100mm', height: '298mm' };
    }
    return { width: '4in', height: '10in' };
  };

  const shouldDownscaleBadge = paperSize === PAPER_SIZE.A4 && !isJapanese;

  return (
    <Document>
      <Page size={getPaperSize(paperSize)}>
        {shouldDisplayDividerFor.includes(paperSize) && (
          <BadgePageDivider
            variant={paperSize === PAPER_SIZE.W298_X_100 || paperSize === PAPER_SIZE.FOUR_X_TEN_INCH ? 'double' : 'four'}
          />
        )}
        <View style={[is4x5inch || is105x140mm ? pdfStyles.row4x5Inches : pdfStyles.row]}>
          {!isCustomBadge && isJapanese && (
            <>
              <JapaneseA4Divider position={JapaneseA4DividerPositions.LEFT} />
              <JapaneseA4Divider position={JapaneseA4DividerPositions.RIGHT} />
              <JapaneseA4Divider position={JapaneseA4DividerPositions.TOP} />
              <JapaneseA4Divider position={JapaneseA4DividerPositions.BOTTOM} />
            </>
          )}
          {isCustomBadge ? (
            <BadgeElement
              rotateAngle={paperSize === PAPER_SIZE.W298_X_100 ? 180 : 0}
              type={type}
              isCustomBadge={isCustomBadge}
              badgeElementViewOptions={footerInfo}
              badgeInfo={badgeInfo}
              badgeSuffix={badgeSuffix}
              paperSize={paperSize}
              imageSrc={base64Images?.logo!}
              qrCodeUrl={qrCodeUrl}
              badgeIcons={badgeIcons}
              additionalStyles={{ borderBottom: 'none' }}
              shouldDownscaleBadge={shouldDownscaleBadge}
            />
          ) : (
            <>
              <View style={[pdfStyles.badgeItem, pdfStyles.noBorderBottom]}>
                <Image source={PDFUtils.returnSrc(base64Images?.image2!)} style={pdfStyles.eventImage} />
              </View>
              <View style={[pdfStyles.badgeItem, pdfStyles.thirdRowItem]}>
                <Image source={PDFUtils.returnSrc(base64Images?.image3!)} style={pdfStyles.eventImage} />
              </View>
            </>
          )}
        </View>
        {isDoubleBadgeHidden && (
          <View style={pdfStyles.row2}>
            <BadgeElement
              type={type}
              rotateAngle={paperSize === PAPER_SIZE.W298_X_100 ? 360 : 0}
              additionalStyles={isDoubleSided ? { ...pdfStyles.rotatedItem } : pdfStyles.noBorderBottom}
              isCustomBadge={isDoubleSided}
              badgeElementViewOptions={footerInfo}
              badgeInfo={badgeInfo}
              badgeSuffix={badgeSuffix}
              paperSize={paperSize}
              imageSrc={base64Images?.logo!}
              qrCodeUrl={qrCodeUrl}
              badgeIcons={badgeIcons}
              shouldDownscaleBadge={shouldDownscaleBadge}
            />
            {!isDoubleSided &&
              (doubleSidedBadge ? (
                <>
                  <BadgeElement
                    type={type}
                    isCustomBadge={isDoubleSided}
                    badgeElementViewOptions={footerInfo}
                    badgeInfo={badgeInfo}
                    badgeSuffix={badgeSuffix}
                    paperSize={paperSize}
                    imageSrc={base64Images?.logo!}
                    qrCodeUrl={qrCodeUrl}
                    additionalStyles={{ ...pdfStyles.noBorderBottom }}
                    badgeIcons={badgeIcons}
                    shouldDownscaleBadge={shouldDownscaleBadge}
                  />
                </>
              ) : (
                <View style={[pdfStyles.badgeItem, pdfStyles.secondRowItem]}>
                  <Image src={PDFUtils.returnSrc(base64Images?.image1!)} style={pdfStyles.eventImage} />
                </View>
              ))}
          </View>
        )}

        {!isCustomBadge && !isJapanese && (
          <>
            <View style={[pdfStyles.secondArrowWrapper]}>
              <Image src={arrowTwo} style={[pdfStyles.secondArrow]} />
              <Text style={[pdfStyles.secondArrowText]}>2</Text>
            </View>
            <View style={[pdfStyles.firstArrowWrapper]}>
              <Text style={[pdfStyles.firstArrowText]}>1</Text>
              <Image src={arrowOne} style={[pdfStyles.firstArrow]} />
            </View>
          </>
        )}
      </Page>
    </Document>
  );
};

export default PDFDocument;
